import { Button, Flex, Group, NumberFormatter, Text } from "@mantine/core";
import { IconCheck, IconRepeat, IconSearch } from "@tabler/icons-react";
import { Chess } from "chess.js";
import { useState } from "react";
import { fetchLichessLink } from "./fetchLichessLink";
import { Evaluation, Level, Mode, Position, ShouldReset, State, User } from "./models";
import { useSettingsStorage, useStorage } from "./ZustandState";
import cn from "classnames";
import { CountUp } from "use-count-up";
import { useDisclosure, usePrevious } from "@mantine/hooks";
import StockfishInfoModal from "./StockfishInfoModal";
import { formatEvaluation, getOpponentLevelToDisplay } from "./utils";
import _ from "lodash";
import HistoryTable from "./HistoryTable";

interface Props {
    game: Chess;
    onBack: () => void;
    resetPosition: () => void;
    shouldReset: ShouldReset;
    state: State;
    mode: Mode;
    setState: (state: State) => void;
    level: Level;
    ratingGain: number | undefined;
    user: User | undefined;
    onRepeatLine: () => void;
    selectedPosition: Position | undefined;
}

const getEvaluation = (evaluations: Evaluation[]) => {
    return _.last(_.values(evaluations))?.evaluation;
};

const PlayingPanelMobile: React.FC<Props> = ({
    game,
    resetPosition,
    shouldReset,
    state,
    mode,
    level,
    ratingGain,
    user,
    onRepeatLine,
    selectedPosition,
}) => {
    const [isLoadingLichessLink, setIsLoadingLichessLink] = useState(false);
    const [stockfishModalOpened, { open: openStockfishModal, close: closeStockfishModal }] = useDisclosure(false);
    const playerColor = useStorage((state) => state.playerColor);
    const previousSelectedPosition = usePrevious(selectedPosition);
    const evaluations = useStorage((state) => state.evaluations);
    const redoStack = useStorage((state) => state.redoStack);
    const stockfishEval = useStorage((state) => state.stockfishEval);
    const settings = useSettingsStorage((state) => state);

    const handleOnAnalyseOnLichess = async () => {
        setIsLoadingLichessLink(true);
        const windowReference = window.open();
        const gameLink = await fetchLichessLink(game, playerColor, redoStack, true);
        if (windowReference) {
            windowReference.location = gameLink;
        }

        setIsLoadingLichessLink(false);
    };

    return (
        <div className="h-full relative flex-col justify-between flex mt-4">
            {mode === "rated" && (
                <div>
                    <Text c="dimmed">Your rating</Text>
                    <div className="relative flex items-center justify-center space-x-1">
                        <Text fz="h2" mt="-0.4rem">
                            <CountUp
                                key={`${previousSelectedPosition?.rating}-${selectedPosition?.rating}`}
                                isCounting={true}
                                start={(selectedPosition?.rating || 0) - (ratingGain || 0)}
                                end={selectedPosition?.rating}
                                duration={1}
                            />
                        </Text>
                        {state === "attempt_finished" && ratingGain !== undefined && (
                            <Text fw="bold" size="xl" c={ratingGain >= 0 ? "green" : "red"}>
                                <sup>
                                    {ratingGain >= 0 ? "+" : ""}
                                    {ratingGain}
                                </sup>
                            </Text>
                        )}
                    </div>
                    <Text fz="xs" c="dimmed">
                        opponent level: {getOpponentLevelToDisplay(level)}
                    </Text>

                    {settings.useLocalStockfish && false && (
                        <Flex ml="lg" gap="0" dir="column" direction="column" align="flex-start" mt="md">
                            <Group>
                                <Text c="dimmed" fz="xs">
                                    Stockfish eval: {formatEvaluation(stockfishEval?.positionEvaluation)} (Depth:{" "}
                                    {stockfishEval?.depth})
                                </Text>
                                {stockfishEval?.isComplete && <IconCheck size={14} />}
                            </Group>
                            <Text c="dimmed" fz="xs">
                                Time taken: {stockfishEval?.time} ms
                            </Text>
                            <Text c="dimmed" fz="xs">
                                NPS: <NumberFormatter thousandSeparator value={stockfishEval?.nps} />
                            </Text>
                        </Flex>
                    )}
                </div>
            )}

            <span className={cn("my-4", { invisible: mode === "rated" && state === "playing" })}>
                Eval: {getEvaluation(evaluations)}
            </span>
            <div className="flex items-center justify-center mx-4 space-x-4">
                <Button
                    onClick={handleOnAnalyseOnLichess}
                    color="gray"
                    className={cn("self-center", {
                        invisible: (mode === "rated" || mode === "mistakes") && state === "playing",
                    })}
                    leftSection={<IconSearch />}
                    variant="outline"
                    loading={isLoadingLichessLink}
                >
                    Analyze
                </Button>
                {(mode === "practice" || state === "attempt_finished") && settings.displayStockfishInfo && (
                    <>
                        <Button onClick={openStockfishModal} color="gray" className="self-center" variant="outline">
                            See stockfish info
                        </Button>
                        <StockfishInfoModal opened={stockfishModalOpened} close={closeStockfishModal} />
                    </>
                )}
                {state === "attempt_finished" && user?.isPremium && (
                    <Button
                        onClick={onRepeatLine}
                        color="gray"
                        className="self-center"
                        leftSection={<IconRepeat />}
                        variant="outline"
                    >
                        Repeat line (unrated)
                    </Button>
                )}
            </div>

            {shouldReset.reason === "novelty" && (
                <div className="text-red-300 my-2">No lichess data for current position, please reset.</div>
            )}
            {shouldReset.reason === "wrongMove" && (
                <div className="text-red-300 my-2">
                    Wrong move played. Please follow moves from the selected custom position
                </div>
            )}

            {shouldReset.reason === "wrongMove" && (
                <Button
                    onClick={resetPosition}
                    className={cn("!h-14", { "mt-6": !shouldReset.shouldReset })}
                    color={shouldReset.shouldReset ? "red" : "blue"}
                >
                    Reset
                </Button>
            )}
            <Button
                onClick={() => {
                    resetPosition();
                }}
                className={cn("!h-14 mt-4 [@media(min-width:1023px)]:mt-0", {
                    invisible: state !== "attempt_finished",
                })}
                color="green"
            >
                Next
            </Button>

            {(state === "attempt_finished" || mode === "practice") && (
                <div className="w-full">
                    <HistoryTable game={game} settings={settings} state={state} mode={mode} />
                </div>
            )}
        </div>
    );
};

export default PlayingPanelMobile;
